import { useCallback, useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import toast from 'react-hot-toast'
import type { KeyedMutator } from 'swr'
import useSWR from 'swr'
import { getPaginationAndFilterQueryString } from 'shared/api/utils/get-pagination-and-filter-query-string'
import { featureFlagsClient } from 'shared/feature-flags/client'
import { featureFlagKeys } from 'shared/feature-flags/feature-keys'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import useUser from 'shared/hooks/use-user'
import type { SwrPaginationInterface } from 'shared/types/swr-pagination-interface'
import { NOTIFICATION_API_URL } from 'modules/profile/notifications/api/notification-center'
import { EventTypeEnum } from 'modules/profile/notifications/enums/notification-settings-enum'
import type {
  NotificationsApiInterface,
  NotificationsFilterInterface,
} from 'modules/profile/notifications/types/notification-types'

interface UseNotificationsInterface extends SwrPaginationInterface {
  shouldFetch?: boolean
  filter: NotificationsFilterInterface
}

export interface HasUnreadResponse {
  hasUnread: boolean
}

export const useNotifications = ({
  limit,
  shouldFetch = true,
  pagination,
  filter,
}: UseNotificationsInterface): {
  notificationsMeta: HasUnreadResponse | undefined
  mutateNotificationsMeta: KeyedMutator<HasUnreadResponse>
  notifications: NotificationsApiInterface | undefined
  isFetching: boolean
  mutate: KeyedMutator<NotificationsApiInterface>
} => {
  const { t } = useTranslation()
  const isReportsEnabled = featureFlagsClient.isOn(featureFlagKeys.enableCommunityReports)

  const notFoundResponseHandler = useCallback(() => {
    toast.error(t('profile.notifications.error_loading_notifications'))
  }, [t])

  const { user } = useUser()
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    NotificationsApiInterface
  >({
    method: RequestMethodsEnum.get,
    notFoundResponseHandler,
  })

  const { fetcher: unreadFetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    HasUnreadResponse
  >({
    method: RequestMethodsEnum.get,
    notFoundResponseHandler,
  })

  const {
    data: notifications,
    mutate,
    isLoading,
  } = useSWR<NotificationsApiInterface>(() => {
    if (user && shouldFetch) {
      return `${NOTIFICATION_API_URL}/notifications?${getPaginationAndFilterQueryString({
        limit,
        pagination,
        filter: { ...filter },
      })}`
    }
    return null
  }, fetcher)

  const {
    data: notificationsMeta,
    mutate: mutateNotificationsMeta,
    isLoading: isLoadingNotificationsMeta,
  } = useSWR<HasUnreadResponse>(() => {
    if (user) {
      return `${NOTIFICATION_API_URL}/notifications/has-unread`
    }
    return null
  }, unreadFetcher)

  const visibleNotifications = useMemo(() => {
    if (!notifications || isReportsEnabled) return notifications
    return {
      ...notifications,
      items: notifications.items.filter(
        item => item.eventType !== EventTypeEnum.REPORTED_CONTENT_ALERT,
      ),
    }
  }, [isReportsEnabled, notifications])

  return {
    notificationsMeta,
    mutateNotificationsMeta,
    notifications: visibleNotifications,
    isFetching: isLoading || isLoadingNotificationsMeta,
    mutate,
  }
}
